import _range from 'lodash/range'

import commonTextsDe from '@flows/configs/translations/de/commonTexts.json'
import commonTextsEn from '@flows/configs/translations/en/commonTexts.json'
import type { TranslationKey } from '@flows/core/i18n/types'
import type { TransformedOption } from '@flows/core/types/stepContentTypes'

export type ChoiceOptionValue = TransformedOption['value']

type ChoiceOption = Pick<TransformedOption, 'value' | 'label'>

export const isKeyInTranslations = (
  translations: Record<TranslationKey, string>[],
  key: TranslationKey
) => translations.every((translation) => Object.keys(translation).includes(key))

export const getChoiceOptions = (
  values: ChoiceOptionValue[],
  translations: Record<TranslationKey, string>[],
  translationKey: TranslationKey,
  namespacePrefix?: string
): ChoiceOption[] => {
  if (!values) {
    throw new Error('"values" is a required parameter')
  }

  if (!translations) {
    throw new Error('"translations" is a required parameter')
  }

  if (!translationKey) {
    throw new Error('"translationKey" is a required parameter')
  }

  return values.map((value, i) => {
    let label = `${translationKey}.${value.toString()}`

    // if the '[key].last' key for alternative label is provided we should use it for the last item translation
    if (i === values.length - 1) {
      const lastLabelKey = `${translationKey}.${value.toString()}.last`
      if (isKeyInTranslations(translations, lastLabelKey)) {
        label = lastLabelKey
      }
    }

    if (!isKeyInTranslations(translations, label)) {
      throw new Error(`Provided translation is missing the following key: ${value.toString()}`)
    }

    const labelTranslationKey = namespacePrefix ? `${namespacePrefix}:${label}` : label

    return {
      label: {
        type: 'translatableText',
        translationKey: labelTranslationKey,
      },
      value,
    }
  })
}
export const getClaimsOptions = (
  maxNumber: number,
  noStringFormat?: boolean
): ReturnType<typeof getChoiceOptions> =>
  noStringFormat
    ? getChoiceOptions(
        _range(maxNumber + 1),
        [commonTextsDe, commonTextsEn],
        'generic.claims_options',
        'sharedFlows_common'
      )
    : getChoiceOptions(
        _range(maxNumber + 1).map((i) => i.toString()),
        [commonTextsDe, commonTextsEn],
        'generic.claims_options',
        'sharedFlows_common'
      )
